import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  ListSubheader,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  Badge,
  useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import SortIcon from '@mui/icons-material/Sort';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import TuneIcon from '@mui/icons-material/Tune';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { filterContracts } from '../../../redux/actions/contractAction';

const SearchFilterBar = ({
  searchTerm,
  handleSearch,
  activeView,
  setActiveView,
  activeFilter,
  setActiveFilter,
  sortOrder,
  setSortOrder,
  currentUser,
  onAdvancedFilter, // New prop for handling advanced filter results
  viewContext = 'contracts', // 'contracts' | 'regulatory_alerts' | 'contract_lifecycle'
  sidebarFilterParams = null, // Current sidebar filter context for composing filters
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Filter menu state
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [isAdvancedFilterOpen, setIsAdvancedFilterOpen] = useState(false);
  const [activeAdvancedFilters, setActiveAdvancedFilters] = useState(0);

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  // Advanced filter handlers
  const handleOpenAdvancedFilter = () => {
    setIsAdvancedFilterOpen(true);
  };

  const handleCloseAdvancedFilter = () => {
    setIsAdvancedFilterOpen(false);
  };

  const handleAdvancedFilter = (filteredContracts) => {
    // Update active filter count based on filter criteria
    setActiveAdvancedFilters(prevCount => prevCount > 0 ? prevCount + 1 : 1);

    // Pass filtered contracts to parent component
    if (onAdvancedFilter) {
      onAdvancedFilter(filteredContracts);
    }
  };

  // Helper: merge filter params with active sidebar context
  const mergeWithSidebarContext = (filterParams) => {
    if (!sidebarFilterParams) return filterParams;
    return { ...sidebarFilterParams, ...filterParams };
  };

  // Handle the last 30 days filter using advanced filter API
  const handleLast30DaysFilter = async () => {
    setActiveFilter('recent');
    handleFilterClose();

    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const filterParams = mergeWithSidebarContext({
      updatedDate: { start: thirtyDaysAgo.toISOString() }
    });

    try {
      const filteredContracts = await dispatch(filterContracts(filterParams));
      setActiveAdvancedFilters(1);
      if (onAdvancedFilter) onAdvancedFilter(filteredContracts);
    } catch (error) {
      console.error('Error applying last 30 days filter:', error);
    }
  };

  // Handle the created by me filter using advanced filter API
  const handleCreatedByMeFilter = async () => {
    setActiveFilter('created');
    handleFilterClose();

    const filterParams = mergeWithSidebarContext({ createdByMe: true });

    try {
      const filteredContracts = await dispatch(filterContracts(filterParams));
      setActiveAdvancedFilters(1);
      if (onAdvancedFilter) onAdvancedFilter(filteredContracts);
    } catch (error) {
      console.error('Error applying created by me filter:', error);
    }
  };

  // Handle status-based filters
  const handleStatusFilter = async (statusValue, filterKey) => {
    setActiveFilter(filterKey);
    handleFilterClose();
    try {
      const filterParams = mergeWithSidebarContext({ status: [statusValue] });
      const filteredContracts = await dispatch(filterContracts(filterParams));
      setActiveAdvancedFilters(1);
      if (onAdvancedFilter) onAdvancedFilter(filteredContracts);
    } catch (error) { console.error('Error applying status filter:', error); }
  };

  // Handle shared with me filter
  const handleSharedWithMeFilter = async () => {
    setActiveFilter('shared');
    handleFilterClose();
    try {
      const filterParams = mergeWithSidebarContext({ sharedWithMe: true });
      const filteredContracts = await dispatch(filterContracts(filterParams));
      setActiveAdvancedFilters(1);
      if (onAdvancedFilter) onAdvancedFilter(filteredContracts);
    } catch (error) { console.error('Error applying shared filter:', error); }
  };

  // Handle smart view filters via filter dropdown
  const handleSmartViewFilter = async (smartViewKey, filterKey) => {
    setActiveFilter(filterKey);
    handleFilterClose();
    try {
      const filterParams = mergeWithSidebarContext({ smartView: smartViewKey });
      const filteredContracts = await dispatch(filterContracts(filterParams));
      setActiveAdvancedFilters(1);
      if (onAdvancedFilter) onAdvancedFilter(filteredContracts);
    } catch (error) { console.error('Error applying smart view filter:', error); }
  };

  // Clear all filters
  const clearAllFilters = async () => {
    setActiveFilter('none');
    setActiveAdvancedFilters(0);
    handleFilterClose();

    try {
      // If sidebar context is active, restore it; otherwise clear everything
      const filterParams = sidebarFilterParams || {};
      const allContracts = await dispatch(filterContracts(filterParams));
      if (onAdvancedFilter) onAdvancedFilter(allContracts);
    } catch (error) {
      console.error('Error clearing filters:', error);
    }
  };

  // Apply a specific sort order
  const applySort = (sort) => {
    setSortOrder(sort);
    handleSortClose();
  };

  // Clear search input
  const clearSearch = () => {
    handleSearch({ target: { value: '' } });
  };

  // Get filter button label
  const getFilterButtonLabel = () => {
    switch (activeFilter) {
      case 'recent': return t('searchFilterBar.filters.last30Days');
      case 'created': return t('searchFilterBar.filters.createdByMe');
      default: return t('searchFilterBar.filter');
    }
  };

  // Get sort button label
  const getSortButtonLabel = () => {
    switch (sortOrder) {
      case 'date_desc': return t('searchFilterBar.sorting.newestFirst');
      case 'date_asc': return t('searchFilterBar.sorting.oldestFirst');
      case 'title_asc': return t('searchFilterBar.sorting.titleAsc');
      case 'title_desc': return t('searchFilterBar.sorting.titleDesc');
      default: return t('searchFilterBar.sort');
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          py: { xs: 2, sm: 2 },
          px: { xs: 2, sm: 1 },
          bgcolor: 'background.paper',
          borderRadius: '12px',
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: theme.palette.mode === 'dark' ? '0 1px 3px rgba(0, 0, 0, 0.2)' : '0 1px 3px rgba(0, 0, 0, 0.05)',
          flexWrap: 'nowrap',
          overflow: 'visible',
          height: { xs: 'auto', sm: '64px' },
          gap: { xs: 2, sm: 0 }
        }}
      >
        <TextField
          fullWidth
          placeholder={t('searchFilterBar.searchPlaceholder')}
          variant="outlined"
          value={searchTerm}
          onChange={handleSearch}
          size="small"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label={t('searchFilterBar.clearSearch')}
                  onClick={clearSearch}
                  edge="end"
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
            sx: {
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.common.black, 0.02)
            }
          }}
          sx={{
            flexShrink: 1,
            flexGrow: 1,
            mr: { xs: 0, sm: 2 },
            width: '100%'
          }}
        />

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          gap: 2,
          width: { xs: '100%', sm: 'auto' },
          justifyContent: { xs: 'space-between', sm: 'flex-start' }
        }}>
          {/* Advanced Filter Button */}
          {/* <Tooltip title={t('searchFilterBar.advancedFilter')}>
            <Badge 
              badgeContent={activeAdvancedFilters} 
              color="primary"
              overlap="circular"
              sx={{ 
                '.MuiBadge-badge': {
                  fontSize: '10px',
                  height: '18px',
                  minWidth: '18px'
                }
              }}
            >
              <IconButton
                onClick={handleOpenAdvancedFilter}
                sx={{ 
                  border: '1px solid',
                  borderColor: alpha(theme.palette.common.black, 0.1),
                  borderRadius: 1.5,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.common.black, 0.02)
                  }
                }}
              >
                <TuneIcon />
              </IconButton>
            </Badge>
          </Tooltip> */}

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            endIcon={activeFilter !== 'none' ? (
              <CloseIcon
                sx={{ fontSize: '16px !important', ml: 0.5 }}
                onClick={(e) => { e.stopPropagation(); clearAllFilters(); }}
              />
            ) : undefined}
            onClick={handleFilterClick}
            color={activeFilter !== 'none' ? 'primary' : 'inherit'}
            sx={{
              borderRadius: 1.5,
              py: 1,
              borderColor: activeFilter !== 'none'
                ? theme.palette.primary.main
                : alpha(theme.palette.common.black, 0.1),
              color: activeFilter !== 'none'
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(
                  activeFilter !== 'none'
                    ? theme.palette.primary.main
                    : theme.palette.common.black,
                  0.02
                )
              },
              whiteSpace: 'nowrap'
            }}
          >
            {getFilterButtonLabel()}
          </Button>

          <Button
            variant="outlined"
            startIcon={<SortIcon />}
            onClick={handleSortClick}
            color={sortOrder !== 'none' ? 'primary' : 'inherit'}
            sx={{
              borderRadius: 1.5,
              py: 1,
              borderColor: sortOrder !== 'none'
                ? theme.palette.primary.main
                : alpha(theme.palette.common.black, 0.1),
              color: sortOrder !== 'none'
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: alpha(
                  sortOrder !== 'none'
                    ? theme.palette.primary.main
                    : theme.palette.common.black,
                  0.02
                )
              },
              whiteSpace: 'nowrap'
            }}
          >
            {getSortButtonLabel()}
          </Button>

          {/* View toggles — hidden in monitoring views (always table) */}
          {viewContext === 'contracts' && (
            <Box sx={{
              display: 'flex',
              ml: 1
            }}>
              <Tooltip title={t('searchFilterBar.kanbanView', 'Kanban')}>
                <IconButton
                  color={activeView === 'kanban' ? 'primary' : 'default'}
                  onClick={() => setActiveView('kanban')}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: activeView === 'kanban' ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                  }}
                >
                  <ViewKanbanOutlinedIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('searchFilterBar.gridView')}>
                <IconButton
                  color={activeView === 'grid' ? 'primary' : 'default'}
                  onClick={() => setActiveView('grid')}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: activeView === 'grid' ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                  }}
                >
                  <GridViewIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('searchFilterBar.tableView', 'Table view')}>
                <IconButton
                  color={activeView === 'table' ? 'primary' : 'default'}
                  onClick={() => setActiveView('table')}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: activeView === 'table' ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                  }}
                >
                  <TableRowsOutlinedIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title={t('searchFilterBar.listView')}>
                <IconButton
                  color={activeView === 'list' ? 'primary' : 'default'}
                  onClick={() => setActiveView('list')}
                  sx={{
                    borderRadius: 1.5,
                    bgcolor: activeView === 'list' ? alpha(theme.palette.primary.main, 0.1) : 'transparent'
                  }}
                >
                  <ViewListIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            elevation: 2,
            sx: { borderRadius: 2, mt: 1, minWidth: 220, maxHeight: 420 }
          }}
        >
          {/* Time & Ownership */}
          <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>
            {t('searchFilterBar.filters.general', 'General')}
          </ListSubheader>
          <MenuItem onClick={handleLast30DaysFilter} selected={activeFilter === 'recent'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <CalendarTodayIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18 }} />
            {t('searchFilterBar.filters.last30Days')}
          </MenuItem>
          <MenuItem onClick={handleCreatedByMeFilter} selected={activeFilter === 'created'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <PersonIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18 }} />
            {t('searchFilterBar.filters.createdByMe')}
          </MenuItem>
          <MenuItem onClick={handleSharedWithMeFilter} selected={activeFilter === 'shared'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <PeopleOutlineIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18 }} />
            {t('searchFilterBar.filters.sharedWithMe', 'Shared with me')}
          </MenuItem>

          {/* By Status */}
          <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mt: 0.5 }}>
            {t('searchFilterBar.filters.byStatus', 'By Status')}
          </ListSubheader>
          <MenuItem onClick={() => handleStatusFilter('DRAFT', 'drafts')} selected={activeFilter === 'drafts'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <EditOutlinedIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18 }} />
            {t('searchFilterBar.filters.drafts', 'Drafts')}
          </MenuItem>
          <MenuItem onClick={() => handleStatusFilter('IN_REVIEW', 'in_review')} selected={activeFilter === 'in_review'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <RateReviewOutlinedIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18 }} />
            {t('searchFilterBar.filters.inReview', 'In Review')}
          </MenuItem>
          <MenuItem onClick={() => handleStatusFilter('PENDING_SIGNATURE', 'pending_sig')} selected={activeFilter === 'pending_sig'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <DrawOutlinedIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18 }} />
            {t('searchFilterBar.filters.pendingSignature', 'Pending Signature')}
          </MenuItem>
          <MenuItem onClick={() => handleStatusFilter('SIGNED', 'signed')} selected={activeFilter === 'signed'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <CheckCircleOutlineIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18 }} />
            {t('searchFilterBar.filters.signed', 'Signed')}
          </MenuItem>

          {/* Monitoring */}
          <ListSubheader sx={{ bgcolor: 'transparent', lineHeight: '28px', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mt: 0.5 }}>
            {t('searchFilterBar.filters.monitoring', 'Monitoring')}
          </ListSubheader>
          <MenuItem onClick={() => handleSmartViewFilter('expiring_30', 'expiring')} selected={activeFilter === 'expiring'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <TimerOutlinedIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18, color: 'warning.main' }} />
            {t('searchFilterBar.filters.expiringSoon', 'Expiring in 30 days')}
          </MenuItem>
          <MenuItem onClick={() => handleSmartViewFilter('auto_renewing', 'auto_renew')} selected={activeFilter === 'auto_renew'} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            <AutorenewIcon fontSize="small" sx={{ mr: 1.5, fontSize: 18, color: 'info.main' }} />
            {t('searchFilterBar.filters.autoRenewing', 'Auto-renewing')}
          </MenuItem>

          <Divider sx={{ my: 0.5 }} />
          <MenuItem onClick={clearAllFilters} sx={{ fontSize: '0.85rem', py: 0.75 }}>
            {t('searchFilterBar.filters.clearFilters')}
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={sortAnchorEl}
          open={Boolean(sortAnchorEl)}
          onClose={handleSortClose}
          PaperProps={{
            elevation: 2,
            sx: { borderRadius: 2, mt: 1, minWidth: 200 }
          }}
        >
          <MenuItem
            onClick={() => applySort('date_desc')}
            selected={sortOrder === 'date_desc'}
          >
            {t('searchFilterBar.sorting.dateNewest')}
          </MenuItem>
          <MenuItem
            onClick={() => applySort('date_asc')}
            selected={sortOrder === 'date_asc'}
          >
            {t('searchFilterBar.sorting.dateOldest')}
          </MenuItem>
          <MenuItem
            onClick={() => applySort('title_asc')}
            selected={sortOrder === 'title_asc'}
          >
            {t('searchFilterBar.sorting.titleAsc')}
          </MenuItem>
          <MenuItem
            onClick={() => applySort('title_desc')}
            selected={sortOrder === 'title_desc'}
          >
            {t('searchFilterBar.sorting.titleDesc')}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => applySort('none')}>
            {t('searchFilterBar.sorting.clearSorting')}
          </MenuItem>
        </Menu>

        {/* Advanced Filter Drawer */}
        {/* <ContractFilterDrawer 
        open={isAdvancedFilterOpen}
        onClose={handleCloseAdvancedFilter}
        onFilter={handleAdvancedFilter}
      /> */}

      </Box>
    </>
  );
};

export default SearchFilterBar;